import { debouncedRef, useLocalStorage } from "@vueuse/core";
import { convert } from "geo-coordinates-parser";

const MIN_SEARCH_LENGTH = 3;
const HISTORY_ENTRIES = 3;

export function useSearch(queryRef, selected, locationRef) {
  const query = debouncedRef(queryRef, 200);
  const mapkitEnabled = ref(true);
  const results = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const searchHistory = useSafeLocalStorage("elevation-search-history", []);
  const { autocomplete } = useMapkit();

  // plain decimal degrees of any precision, e.g. "40.71, -74.01"; the parser below needs at least 4 decimals
  const DECIMAL_COORDINATES = /^\s*([+-]?\d+(?:\.\d+)?)\s*(?:,\s*|\s+)([+-]?\d+(?:\.\d+)?)\s*$/;

  function latLngResult(latitude, longitude, name) {
    return [
      {
        location: { latitude, longitude, name },
        name,
        module: "latLng",
        type: "location",
      },
    ];
  }

  function latLngSearch(query) {
    const decimal = query.match(DECIMAL_COORDINATES);
    if (decimal) {
      const [latitude, longitude] = [parseFloat(decimal[1]), parseFloat(decimal[2])];
      if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        return [];
      }
      return latLngResult(latitude, longitude, `${decimal[1]}, ${decimal[2]}`);
    }
    try {
      const converted = convert(query);
      const name = `${converted.verbatimLatitude}, ${converted.verbatimLongitude}`;
      return latLngResult(converted.decimalLatitude, converted.decimalLongitude, name);
    } catch {
      return [];
    }
  }

  function currentLocationSearch(query) {
    if (query.length > 0) {
      return [];
    }
    return [
      {
        name: "Use Current Location",
        module: "currentLocation",
        type: "currentLocation",
      },
    ];
  }

  function recentSearches(query) {
    if (query.length >= MIN_SEARCH_LENGTH) {
      return [];
    }
    return searchHistory.value.map((item) => ({ ...item, module: "history" }));
  }

  async function mapkitSearch(query) {
    if (query.length < MIN_SEARCH_LENGTH || !mapkitEnabled.value) {
      return [];
    }
    try {
      const data = await autocomplete(query, locationRef.value);
      return data
        .map((item) => {
          return {
            location: {
              ...item.location,
              name: item.displayLines.join(", "),
            },
            name: item.displayLines[0],
            subtitle: item.displayLines.slice(1).join(", "),
            module: "mapkitSearch",
            type: "location",
          };
        })
        .slice(0, 5);
    } catch (error) {
      console.error(error);
      mapkitEnabled.value = false;
      return [];
    }
  }

  const searchModules = [latLngSearch, mapkitSearch, recentSearches, currentLocationSearch];

  async function search() {
    isLoading.value = true;
    error.value = null;
    try {
      const searchResponse = await Promise.all(searchModules.map((fn) => fn(query.value)));
      results.value = searchResponse.flat();
    } catch (err) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }
  }

  watch(selected, () => {
    if (["currentLocation", "history"].includes(selected.value?.module) || !selected.value?.location) {
      return;
    }

    // add selected to start of history
    searchHistory.value.unshift(selected.value);

    // remove duplicates and limit number of entries
    searchHistory.value = searchHistory.value
      .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name))
      .slice(0, HISTORY_ENTRIES);
  });

  watch(query, search);
  search();

  return {
    results,
    isLoading,
    error,
  };
}
